import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Firebase import removed - using Worker instead
// import { auth } from '../firebase'; // For logout
import { User, PencilSimple, FloppyDisk, X, SignOut } from '@phosphor-icons/react';
import LoadingOverlay from '../components/LoadingOverlay';

// --- Options (Copied from Profile.jsx) ---
const concernOptions = ['جوش', 'لک', 'چروک', 'حساسیت', 'خشکی', 'منافذ', 'کدری', 'هایپرپیگمنتیشن'];
const iranianBrandsOptions = [
  'سینره', 'شون', 'نئودرم', 'مای', 'سی گل', 'درماسیف', 'پریم', 'هیدرودرم', 'سایر'
];
const internationalBrandsOptions = [
  'CeraVe', 'The Ordinary', 'La Roche-Posay', 'Vichy', 'Bioderma', 'Neutrogena', "Paula's Choice", 'L\'Oréal', 'COSRX', 'Eucerin', 'سایر'
];
// --- End Options ---


// Worker URL (Move to config/env)
const WORKER_URL = process.env.REACT_APP_API_BASE_URL || 'https://empty-rain-2654.er-hooman.workers.dev';

// Spinner Component
const Spinner = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black inline-block align-middle"></div>
);

// --- Initial Empty State Structure ---
const initialFormData = {
  fullName: '',
  firstName: '', // Keep firstName/lastName if API uses them, otherwise rely on fullName
  lastName: '',
  age: '',
  gender: '',
  city: '',
  job: '',
  email: '', // Usually fetched, not editable
  phoneNumber: '',
  skinType: '',
  skinTone: '',
  concerns: [],
  knownBrands: {
    iranian: [],
    international: [],
  },
  lifestyle: {
    exercise: '',
    smoking: '',
    stress: '',
    sleep: '',
    doctor: '',
    diet: '',
  },
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { userId, token, logout } = useAuth();

  const [profileData, setProfileData] = useState(null); // Raw data from API
  const [formData, setFormData] = useState(initialFormData); // Data for the form
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null); // Fetch error
  const [saveError, setSaveError] = useState(null); // Save error

  // --- Reusable Fetch with Auth ---
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!userId || !token) {
      throw new Error("User ID or auth token missing");
    }
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response;
  }, [userId, token]);

  // --- Initialize Form Data from API Data ---
  const initializeForm = useCallback((apiData) => {
    if (!apiData) {
      setFormData(initialFormData);
      return;
    }
    setFormData({
      fullName: apiData.fullName || '',
      firstName: apiData.firstName || '',
      lastName: apiData.lastName || '',
      age: apiData.age || '',
      gender: apiData.gender || '',
      city: apiData.city || '',
      job: apiData.job || '',
      email: apiData.email || '', // Display only
      phoneNumber: apiData.phoneNumber || '',
      skinType: apiData.skinType || '',
      skinTone: apiData.skinTone || '',
      concerns: apiData.concerns || [],
      knownBrands: {
        iranian: apiData.knownBrands?.iranian || [],
        international: apiData.knownBrands?.international || [],
      },
      lifestyle: {
        exercise: apiData.lifestyle?.exercise || '',
        smoking: apiData.lifestyle?.smoking || '',
        stress: apiData.lifestyle?.stress || '',
        sleep: apiData.lifestyle?.sleep || '',
        doctor: apiData.lifestyle?.doctor || '',
        diet: apiData.lifestyle?.diet || '',
      },
    });
  }, []);


  // --- Fetch Profile Data ---
  const fetchProfile = useCallback(async () => {
    if (!userId) {
        console.log("ProfilePage: No userId yet, skipping fetch.");
        setIsLoading(false); // Stop loading if no user
        return;
    }
    console.log("ProfilePage: Fetching profile for userId:", userId);
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${WORKER_URL}/api/users/me/profile`);
      if (response.ok) {
        const data = await response.json();
        console.log("ProfilePage: Profile data fetched:", data);
        setProfileData(data); // Store raw API data
        initializeForm(data); // Initialize form state based on fetched data
      } else {
        console.error("ProfilePage: Failed to fetch profile", response.status);
        setError(`خطا در دریافت اطلاعات پروفایل (${response.status})`);
        setProfileData(null);
        initializeForm(null); // Reset form on error
      }
    } catch (err) {
      console.error("ProfilePage: Network error fetching profile", err);
      setError('خطای شبکه در ارتباط با سرور.');
      setProfileData(null);
      initializeForm(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId, fetchWithAuth, initializeForm]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // --- Handle Input Change (Simple & Nested) ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleLifestyleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      lifestyle: { ...(prevData.lifestyle || {}), [name]: value },
    }));
  };

  // --- Handle Multi-Select Toggles ---
  const handleToggleSelection = (item, type) => {
    setFormData(prevData => {
      const updatedData = { ...prevData };
      if (type === 'concerns') {
        const currentSelection = prevData.concerns || [];
        updatedData.concerns = currentSelection.includes(item)
          ? currentSelection.filter(c => c !== item)
          : [...currentSelection, item];
      } else { // Handle brands
        const brandType = type; // 'iranian' or 'international'
        const updatedBrands = { ...(prevData.knownBrands || { iranian: [], international: [] }) };
        const currentSelection = updatedBrands[brandType] || [];
        updatedBrands[brandType] = currentSelection.includes(item)
          ? currentSelection.filter(b => b !== item)
          : [...currentSelection, item];
        updatedData.knownBrands = updatedBrands;
      }
      return updatedData;
    });
  };


  // --- Handle Edit Toggle ---
  const handleEditToggle = () => {
    if (isEditing) {
      // If canceling edit, reset form data to original *fetched* data
      initializeForm(profileData);
      setSaveError(null);
    }
    setIsEditing(!isEditing);
  };

  // --- Handle Save Profile ---
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!userId) return;
    console.log("ProfilePage: Saving profile data via API:", formData);
    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = formData; // Assuming formData holds the complete structure

      const response = await fetchWithAuth(`${WORKER_URL}/api/users/me/profile`, {
        method: 'PUT', // Or PATCH
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        console.log("ProfilePage: Profile saved successfully via API. Updating state with local formData.");
        setProfileData(formData); // Update raw data state with the data we sent
        initializeForm(formData); // Re-initialize form state with the data we sent
        setIsEditing(false);
      } else {
        const errorData = await response.json().catch(() => ({ message: `خطا در ذخیره سازی (${response.status})` }));
        console.error("ProfilePage: Failed to save profile via API", response.status, errorData);
        setSaveError(errorData.message || `خطا در ذخیره سازی (${response.status})`);
      }
    } catch (err) {
      console.error("ProfilePage: Network error saving profile via API", err);
      setSaveError('خطای شبکه در ارتباط با سرور هنگام ذخیره سازی.');
    } finally {
      setIsSaving(false);
    }
  };

  // --- Handle Logout ---
  const handleLogout = async () => {
      console.log("ProfilePage: Logging out...");
      try {
        // Use AuthContext logout instead of Firebase auth directly
        logout();
        localStorage.removeItem('firebaseIdToken');
        localStorage.removeItem('token');
        // Clear context state immediately
        navigate('/login', { replace: true });
      } catch (error) {
        console.error("ProfilePage: Error logging out:", error);
        setError('خطا در خروج از حساب کاربری.');
      }
  };


  // --- Render Loading/Error States ---
  if (isLoading) {
      return (
          <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
              <Spinner />
          </div>
      );
  }

  if (error) {
      return (
          <div className="text-center p-10 text-red-600">
              <p>{error}</p>
              <button onClick={fetchProfile} className="mt-4 px-4 py-2 bg-black text-white rounded-full text-sm">
                  تلاش مجدد
              </button>
          </div>
      );
  }

  // Even if profileData is null after loading (e.g., 404 or fetch error handled),
  // formData should be initialized, so we can proceed to render the form/display.
  // We might show placeholders if data is missing.

  // --- Render Profile Content ---
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8 font-light relative" dir="rtl">
      <LoadingOverlay 
        isLoading={isLoading || isSaving} 
        message={
          isLoading ? 'در حال بارگذاری پروفایل...' :
          isSaving ? 'در حال ذخیره تغییرات...' :
          'در حال بارگذاری...'
        }
      />
      {/* Profile Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <User size={28} className="ml-2" weight="duotone" /> {/* Adjusted margin for RTL */}
          پروفایل کاربری
        </h1>
        <div className="flex space-x-2 rtl:space-x-reverse">
            {isEditing ? (
              <>
                {/* Cancel Button */}
                <button
                    onClick={handleEditToggle}
                    disabled={isSaving}
                    className="inline-flex items-center px-4 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                    <X size={16} className="ml-1" /> {/* Adjusted margin */}
                    لغو
                </button>
                {/* Save Button is inside the form */}
              </>
            ) : (
              // Edit Button
              <button
                onClick={handleEditToggle}
                className="inline-flex items-center px-4 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilSimple size={16} className="ml-1" /> {/* Adjusted margin */}
                ویرایش
              </button>
            )}
          </div>
      </div>

      {/* Profile Form */}
      {/* Use formData for values */}
      <form onSubmit={handleSaveProfile} className="space-y-8">

        {/* --- Personal Information Section --- */}
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-sm ring-1 ring-gray-900/5">
          <h2 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">اطلاعات شخصی</h2>
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
            {/* Full Name */}
            <div className="sm:col-span-6">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">نام کامل</label>
              {isEditing ? (
                <input type="text" name="fullName" id="fullName" value={formData.fullName} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50"/>
              ) : (
                <p className="text-gray-800 pt-1.5">{formData.fullName || '-'}</p>
              )}
            </div>
             {/* Age */}
            <div className="sm:col-span-3">
              <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">سن</label>
              {isEditing ? (
                <input type="number" name="age" id="age" value={formData.age} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50"/>
              ) : (
                 <p className="text-gray-800 pt-1.5">{formData.age || '-'}</p>
              )}
            </div>
             {/* Gender */}
            <div className="sm:col-span-3">
               <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">جنسیت</label>
                {isEditing ? (
                    <select name="gender" id="gender" value={formData.gender} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                        <option value="">انتخاب کنید</option>
                        <option value="مرد">مرد</option>
                        <option value="زن">زن</option>
                        <option value="دیگر">دیگر</option>
                    </select>
                ) : (
                    <p className="text-gray-800 pt-1.5">{formData.gender || '-'}</p>
                )}
            </div>
             {/* Phone Number */}
            <div className="sm:col-span-3">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">شماره تلفن</label>
               {isEditing ? (
                  <input type="tel" name="phoneNumber" id="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50" placeholder="09123456789"/>
                ) : (
                  <p className="text-gray-800 pt-1.5" dir="ltr">{formData.phoneNumber || 'ثبت نشده'}</p>
                )}
            </div>
             {/* Email (Display Only) */}
            <div className="sm:col-span-3">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">آدرس ایمیل</label>
              <p className="text-gray-500 pt-1.5">{formData.email || 'ایمیل یافت نشد'}</p>
            </div>
             {/* City */}
            <div className="sm:col-span-3">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">شهر</label>
                 {isEditing ? (
                    <input type="text" name="city" id="city" value={formData.city} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50"/>
                ) : (
                    <p className="text-gray-800 pt-1.5">{formData.city || '-'}</p>
                )}
            </div>
            {/* Job */}
            <div className="sm:col-span-3">
                <label htmlFor="job" className="block text-sm font-medium text-gray-700 mb-1">شغل</label>
                {isEditing ? (
                    <input type="text" name="job" id="job" value={formData.job} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50"/>
                ) : (
                    <p className="text-gray-800 pt-1.5">{formData.job || '-'}</p>
                )}
            </div>
          </div>
        </div>

        {/* --- Skin Information Section --- */}
         <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h2 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">اطلاعات پوست</h2>
            <div className="space-y-6">
                {/* Skin Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نوع پوست</label>
                     {isEditing ? (
                        <select name="skinType" value={formData.skinType} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                            <option value="">انتخاب کنید</option>
                            <option value="چرب">چرب</option>
                            <option value="خشک">خشک</option>
                            <option value="مختلط">مختلط</option>
                            <option value="نرمال">نرمال</option>
                            <option value="حساس">حساس</option>
                        </select>
                    ) : (
                        <p className="text-gray-800 pt-1.5">{formData.skinType || '-'}</p>
                    )}
                </div>
                 {/* Skin Tone */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">رنگ پوست</label>
                    {isEditing ? (
                        <select name="skinTone" value={formData.skinTone} onChange={handleInputChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                             <option value="">انتخاب کنید</option>
                             <option value="بسیار روشن">بسیار روشن</option>
                             <option value="روشن">روشن</option>
                             <option value="گندمی روشن">گندمی روشن</option>
                             <option value="گندمی تیره">گندمی تیره</option>
                             <option value="سبزه">سبزه</option>
                             <option value="تیره">تیره</option>
                        </select>
                    ) : (
                        <p className="text-gray-800 pt-1.5">{formData.skinTone || '-'}</p>
                    )}
                </div>
                 {/* Concerns */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">مشکلات پوستی</label>
                    <div className="flex flex-wrap gap-2">
                        {isEditing ? (
                            concernOptions.map((concern) => (
                                <button key={concern} type="button" onClick={() => handleToggleSelection(concern, 'concerns')} disabled={isSaving}
                                    className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${formData.concerns?.includes(concern) ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} disabled:opacity-50`}
                                >{concern}</button>
                            ))
                        ) : (
                             formData.concerns?.length > 0
                                ? formData.concerns.map((c, i) => <span key={i} className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">{c}</span>)
                                : <p className="text-gray-500 text-sm">وارد نشده</p>
                        )}
                    </div>
                </div>
            </div>
        </div>

        {/* --- Lifestyle Information Section --- */}
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-sm ring-1 ring-gray-900/5">
             <h2 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">سبک زندگی</h2>
             <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                 {/* Exercise */}
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ورزش</label>
                    {isEditing ? (
                      <select name="exercise" value={formData.lifestyle.exercise} onChange={handleLifestyleChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                        <option value="">انتخاب</option> <option value="بله">بله</option> <option value="خیر">خیر</option>
                      </select>
                    ) : ( <p className="text-gray-800 pt-1.5">{formData.lifestyle.exercise || '-'}</p> )}
                  </div>
                 {/* Smoking */}
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">سیگار</label>
                    {isEditing ? (
                       <select name="smoking" value={formData.lifestyle.smoking} onChange={handleLifestyleChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                         <option value="">انتخاب</option> <option value="بله">بله</option> <option value="خیر">خیر</option>
                      </select>
                    ) : ( <p className="text-gray-800 pt-1.5">{formData.lifestyle.smoking || '-'}</p> )}
                  </div>
                 {/* Stress */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">سطح استرس</label>
                    {isEditing ? (
                      <select name="stress" value={formData.lifestyle.stress} onChange={handleLifestyleChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                         <option value="">انتخاب</option> <option value="کم">کم</option> <option value="متوسط">متوسط</option> <option value="زیاد">زیاد</option>
                      </select>
                    ) : ( <p className="text-gray-800 pt-1.5">{formData.lifestyle.stress || '-'}</p> )}
                  </div>
                 {/* Sleep */}
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">کیفیت خواب</label>
                     {isEditing ? (
                       <select name="sleep" value={formData.lifestyle.sleep} onChange={handleLifestyleChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                        <option value="">انتخاب</option> <option value="خوب">خوب</option> <option value="متوسط">متوسط</option> <option value="ضعیف">ضعیف</option>
                      </select>
                    ) : ( <p className="text-gray-800 pt-1.5">{formData.lifestyle.sleep || '-'}</p> )}
                  </div>
                 {/* Doctor */}
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">مراجعه به پزشک</label>
                    {isEditing ? (
                      <select name="doctor" value={formData.lifestyle.doctor} onChange={handleLifestyleChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                         <option value="">انتخاب</option> <option value="بله">بله</option> <option value="خیر">خیر</option>
                      </select>
                    ) : ( <p className="text-gray-800 pt-1.5">{formData.lifestyle.doctor || '-'}</p> )}
                  </div>
                 {/* Diet */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">رژیم غذایی</label>
                    {isEditing ? (
                       <select name="diet" value={formData.lifestyle.diet} onChange={handleLifestyleChange} disabled={isSaving} className="block w-full rounded-md border-gray-300 shadow-sm py-1.5 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white disabled:opacity-50">
                         <option value="">انتخاب</option> <option value="بله">بله</option> <option value="تاحدودی">تاحدودی</option> <option value="خیر">خیر</option>
                      </select>
                    ) : ( <p className="text-gray-800 pt-1.5">{formData.lifestyle.diet || '-'}</p> )}
                  </div>
             </div>
        </div>

        {/* --- Known Brands Section --- */}
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h2 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">برندهای شناخته شده</h2>
             <div className="space-y-6">
                 {/* Iranian Brands */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">برندهای ایرانی</label>
                  <div className="flex flex-wrap gap-2">
                     {isEditing ? (
                         iranianBrandsOptions.map((brand) => (
                             <button key={brand} type="button" onClick={() => handleToggleSelection(brand, 'iranian')} disabled={isSaving}
                                 className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${formData.knownBrands?.iranian?.includes(brand) ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} disabled:opacity-50`}
                             >{brand}</button>
                         ))
                      ) : (
                          formData.knownBrands?.iranian?.length > 0
                              ? formData.knownBrands.iranian.map((b, i) => <span key={i} className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">{b}</span>)
                              : <p className="text-gray-500 text-sm">وارد نشده</p>
                      )}
                  </div>
                </div>
                 {/* International Brands */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">برندهای خارجی</label>
                  <div className="flex flex-wrap gap-2">
                    {isEditing ? (
                        internationalBrandsOptions.map((brand) => (
                            <button key={brand} type="button" onClick={() => handleToggleSelection(brand, 'international')} disabled={isSaving}
                                className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${formData.knownBrands?.international?.includes(brand) ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} disabled:opacity-50`}
                            >{brand}</button>
                         ))
                      ) : (
                          formData.knownBrands?.international?.length > 0
                             ? formData.knownBrands.international.map((b, i) => <span key={i} className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">{b}</span>)
                             : <p className="text-gray-500 text-sm">وارد نشده</p>
                      )}
                  </div>
                </div>
            </div>
        </div>

        {/* Save Button (only in edit mode) */}
        {isEditing && (
          <div className="flex items-center justify-end gap-x-4 pt-4">
             {/* Display Save Error */}
             {saveError && ( <p className="text-sm text-red-600">{saveError}</p> )}
             <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center rounded-full bg-black px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 disabled:opacity-50"
            >
              {isSaving ? (
                 <><Spinner /> <span className="ml-2 rtl:mr-2">در حال ذخیره...</span></>
              ) : (
                 <><FloppyDisk size={16} className="ml-1" /> ذخیره تغییرات</>
              )}
            </button>
          </div>
        )}
      </form>

      {/* Logout Button */}
      <div className="mt-10 pt-6 border-t border-gray-200 flex justify-center">
        <button
          onClick={handleLogout}
          className="inline-flex items-center rounded-full bg-red-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
        >
          <SignOut size={18} className="ml-2" weight="bold" /> {/* Adjusted margin */}
          خروج از حساب کاربری
        </button>
      </div>
    </div>
  );
};

export default ProfilePage; 